import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type LatLng } from '~/_shared/types/latLng';
import { ensureVertexNotOverlapping } from '~/_shared/utils/webgl/mapVertices.helpers';
import { type MapObjectManager } from '../../private/mapObjectManager';
import { useMapObjectManager } from '../../private/useMapObjectManager';
import { type MapShapeInstance } from '../mapShapeModel';

export type DrawMapShapeContextModel = {
  readonly shape: MapShapeInstance | undefined;
  readonly shapeRef: RefObject<MapShapeInstance | undefined>;
  readonly setShape: Dispatch<SetStateAction<MapShapeInstance | undefined>>;
  readonly manager: MapObjectManager;
  readonly addOutline: (latLng: LatLng) => void;
};

const DrawMapShapeContext = createContext<DrawMapShapeContextModel | null>(null);

type DrawMapShapeContextProviderProps = {
  onChange?: (shape: MapShapeInstance) => void;
  children: ReactNode;
};

export const DrawMapShapeContextProvider: FC<DrawMapShapeContextProviderProps> = ({ onChange, children }) => {
  const manager = useMapObjectManager();

  const [shape, setShapeState] = useState<MapShapeInstance | undefined>();
  const shapeRef = useRef<MapShapeInstance | undefined>(undefined);
  shapeRef.current = shape;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setShape = useCallback<Dispatch<SetStateAction<MapShapeInstance | undefined>>>((value) => {
    setShapeState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      shapeRef.current = next;
      return next;
    });
  }, []);

  const addOutline = useCallback((latLng: LatLng) => {
    const currentShape = shapeRef.current;
    if (!currentShape) {
      return;
    }

    const outline = manager.createOutlineFromLatLng(latLng);

    // prevent adding the same outline at last and one before last position
    const outlineExists = !!currentShape.outlines.slice(-2).find(
      shapeOutline => shapeOutline.lat === outline.lat && shapeOutline.lng === outline.lng
    );

    if (outlineExists) {
      return;
    }

    const outlineLatLngWithoutOverlaps = ensureVertexNotOverlapping(outline, currentShape.outlines);
    const updatedShape = {
      ...currentShape,
      outlines: [...currentShape.outlines, { ...outline, ...outlineLatLngWithoutOverlaps }],
    };

    setShape(updatedShape);
    onChangeRef.current?.(updatedShape);
  }, [manager, setShape]);

  const value = useMemo<DrawMapShapeContextModel>(() => ({
    shape,
    shapeRef,
    setShape,
    manager,
    addOutline,
  }), [shape, setShape, manager, addOutline]);

  return (
    <DrawMapShapeContext.Provider value={value} >
      {children}
    </DrawMapShapeContext.Provider>
  );
};

export const useDrawMapShapeContext = (): DrawMapShapeContextModel => {
  const context = useContext(DrawMapShapeContext);

  if (!context) {
    throw new Error('DrawMapShape components cannot be used without the DrawMapShapeContextProvider.');
  }

  return context;
};
